import {
  FileIcon,
  GearIcon,
  HouseIcon,
  PersonIcon,
  BuildingIcon,
  StarIcon,
  TriangleIcon,
  ChartIcon,
  DatabaseIcon,
  FormIcon,
  CalendarIcon,
  OrgChartIcon,
  ChatIcon,
  OfferIcon,
  HierarchyIcon,
  AssessmentIcon
} from '../sidebar-styles'

/**
 * Creates the complete menu structure based on user role and organization type
 * @param {Function} t - Translation function
 * @param {Boolean} isAgency - Whether the user belongs to an agency
 * @param {String} organizationType - Type of organization
 * @param {Object} menuItemsCounter - Counter for menu items notifications
 * @param {Object} theme - Theme constants for styling
 * @param {Object} permissions - User permissions configuration
 * @param {Object} [unitNamingLabels] - Optional labels from useUnitNaming (unitsPanel for /branches)
 * @returns {Array} Menu structure configuration
 */
export const createMenuStructure = (
  t,
  isAgency,
  organizationType,
  menuItemsCounter,
  theme,
  permissions,
  unitNamingLabels = {},
) => [
    createMainMenuSection(t, isAgency, organizationType, permissions, unitNamingLabels),
    createRecruitmentSection(t, menuItemsCounter, theme, permissions),
    createOrganizationSection(
      t,
      organizationType,
      isAgency,
      menuItemsCounter,
      theme,
      permissions,
    ),
    createStrategicPlannerSection(t, permissions),
  ]

/**
 * Creates the main menu section
 * @param {Function} t - Translation function
 * @param {Boolean} isAgency - Whether the user belongs to an agency
 * @param {String} organizationType - Type of organization
 * @param {Object} permissions - User permissions configuration
 * @returns {Object} Main menu section configuration
 */
const createMainMenuSection = (t, isAgency, organizationType, permissions, unitNamingLabels = {}) => ({
  section: t('layout.menu'),
  items: [
    {
      icon: HouseIcon,
      label: t('layout.dashboards'),
      href: '/',
      // permission: permissions.companyDashboard, // All Users should see the dashboard home page
    },
    {
      icon: BuildingIcon,
      label: unitNamingLabels.unitsPanel || t('layout.centralUnitsPanel'),
      href: '/branches',
    },
    ...(isAgency
      ? [createAgencyOrganizationItem(t, organizationType, permissions)]
      : []),
  ],
})

/**
 * Creates the agency organization menu item
 * @param {Function} t - Translation function
 * @param {String} organizationType - Type of organization
 * @param {Object} permissions - User permissions configuration
 * @returns {Object} Agency organization menu item configuration
 */
const createAgencyOrganizationItem = (t, organizationType, permissions) => ({
  icon: BuildingIcon,
  label: t('layout.organizationBoard', { organizationType }),
  href: '/organizations',
  permission: permissions.organizationReadAll,
})

/**
 * Creates the recruitment section
 * @param {Function} t - Translation function
 * @param {Object} menuItemsCounter - Counter for menu items notifications
 * @param {Object} theme - Theme constants for styling
 * @param {Object} permissions - User permissions configuration
 * @returns {Object} Recruitment section configuration
 */
const createRecruitmentSection = (t, menuItemsCounter, theme, permissions) => ({
  section: t('layout.recruitment'),
  items: [
    createJobRequisitionItem(t, menuItemsCounter, theme, permissions),
    createJobRequestItem(t, menuItemsCounter, theme, permissions),
    createEvaluationFormItem(t, menuItemsCounter, theme, permissions),
    createAssessmentsItem(t, menuItemsCounter, theme),
    createPositionItem(t, permissions),
    createCandidatesItem(t, permissions),
    createConversationsItem(t, menuItemsCounter, theme, permissions),
    createOffersItem(t, menuItemsCounter, theme, permissions),
  ],
})

/**
 * Creates the job requisition menu item
 * @param {Function} t - Translation function
 * @param {Object} menuItemsCounter - Counter for menu items notifications
 * @param {Object} theme - Theme constants for styling
 * @param {Object} permissions - User permissions configuration
 * @returns {Object} Job requisition menu item configuration
 */
const createJobRequisitionItem = (t, menuItemsCounter, theme, permissions) => ({
  icon: StarIcon,
  label: t('requisition.jobRequisition'),
  subItems: [
    {
      icon: StarIcon,
      label: `${t('requisition.requisitionManagement')} ${t('requisition.hiringRequests')}`,
      href: '/requisitions',
      permission: permissions.requisitionsReadHistory,
      count: {
        value:
          menuItemsCounter?.hiringRequests >= 100
            ? '99+'
            : menuItemsCounter?.hiringRequests,
        hoverText: `Pending: ${menuItemsCounter?.hiringRequests}`,
        borderColor:
          menuItemsCounter?.hiringRequests === 0 ? theme.blue : theme.appleRed,
        backgroundColor:
          menuItemsCounter?.hiringRequests === 0
            ? theme.persianBlue
            : theme.faluRed,
        className: 'rounded-circle mt-3 text-center',
      },
    },
  ],
})

/**
 * Creates the job request menu item
 * @param {Function} t - Translation function
 * @param {Object} menuItemsCounter - Counter for menu items notifications
 * @param {Object} theme - Theme constants for styling
 * @param {Object} permissions - User permissions configuration
 * @returns {Object} Job request menu item configuration
 */
const createJobRequestItem = (t, menuItemsCounter, theme, permissions) => ({
  icon: FileIcon,
  label: t('requisition.jobRequests'),
  href: '/job-requests',
  permission: permissions.jobRequestsRead,
  count: {
    value:
      menuItemsCounter?.jobRequests >= 100
        ? '99+'
        : menuItemsCounter?.jobRequests,
    hoverText: `Pending: ${menuItemsCounter?.jobRequests}`,
    borderColor:
      menuItemsCounter?.jobRequests === 0 ? theme.blue : theme.appleRed,
    backgroundColor:
      menuItemsCounter?.jobRequests === 0 ? theme.persianBlue : theme.faluRed,
    className: 'rounded-circle mt-1 text-center',
  },
})

/**
 * Creates the evaluation form menu item
 * @param {Function} t - Translation function
 * @param {Object} menuItemsCounter - Counter for menu items notifications
 * @param {Object} theme - Theme constants for styling
 * @param {Object} permissions - User permissions configuration
 * @returns {Object} Evaluation form menu item configuration
 */
const createEvaluationFormItem = (t, menuItemsCounter, theme, permissions) => ({
  icon: FormIcon,
  label: t('evaluationForm.evaluationForms'),
  href: '/evaluation-forms',
  permission: permissions.evaluationFormsReadAll,
  count: {
    value:
      menuItemsCounter?.pendingEvalForms >= 100
        ? '99+'
        : menuItemsCounter?.pendingEvalForms,
    hoverText: `Pending: ${menuItemsCounter?.pendingEvalForms}`,
    borderColor:
      menuItemsCounter?.pendingEvalForms === 0 ? theme.blue : theme.appleRed,
    backgroundColor:
      menuItemsCounter?.pendingEvalForms === 0
        ? theme.persianBlue
        : theme.faluRed,
    className: 'rounded-circle mt-1 text-center',
  },
})

/**
 * Creates the assessments menu item
 * @param {Function} t - Translation function
 * @param {Object} menuItemsCounter - Counter for menu items notifications
 * @param {Object} theme - Theme constants for styling
 * @returns {Object} Assessments menu item configuration
 */
const createAssessmentsItem = (t, menuItemsCounter, theme) => ({
  icon: AssessmentIcon,
  label: t('assessment.assessments'),
  href: '/assessments',
  count: {
    value:
      menuItemsCounter?.pendingAssessments >= 100
        ? '99+'
        : menuItemsCounter?.pendingAssessments,
    hoverText: `Pending: ${menuItemsCounter?.pendingAssessments}`,
    borderColor:
      menuItemsCounter?.pendingAssessments === 0 ? theme.blue : theme.appleRed,
    backgroundColor:
      menuItemsCounter?.pendingAssessments === 0 ? theme.persianBlue : theme.faluRed,
    className: 'rounded-circle mt-1 text-center',
  },
})

/**
 * Creates the position menu item
 * @param {Function} t - Translation function
 * @param {Object} permissions - User permissions configuration
 * @returns {Object} Position menu item configuration
 */
const createPositionItem = (t, permissions) => ({
  icon: TriangleIcon,
  label: t('general.positions'),
  href: '/positions',
  permission: permissions.positionRead,
})

/**
 * Creates the candidates menu item
 * @param {Function} t - Translation function
 * @param {Object} permissions - User permissions configuration
 * @returns {Object} Candidates menu item configuration
 */
const createCandidatesItem = (t, permissions) => ({
  icon: PersonIcon,
  label: t('general.candidates'),
  subItems: [
    {
      icon: PersonIcon,
      label: t('general.candidatesManagement'),
      href: '/local-candidates',
      permission: permissions.candidateReadAll,
    },
    {
      icon: PersonIcon,
      label: t('general.blacklistCandidates'),
      href: '/blacklist-candidates',
      permission: permissions.candidateReadBlacklisted,
    },
    {
      icon: PersonIcon,
      label: t('general.blockedCandidates'),
      href: '/blocked-candidates',
      permission: permissions.candidateReadBlacklisted,
    },
    {
      icon: PersonIcon,
      label: t('talentPool.talentPoolsCandidates'),
      href: '/talent-pools',
      permission: permissions.candidateReadAll,
    },
  ],
})

/**
 * Creates the conversations menu item
 * @param {Function} t - Translation function
 * @param {Object} menuItemsCounter - Counter for menu items notifications
 * @param {Object} theme - Theme constants for styling
 * @param {Object} permissions - User permissions configuration
 * @returns {Object} Conversations menu item configuration
 */
const createConversationsItem = (t, menuItemsCounter, theme, permissions) => ({
  icon: ChatIcon,
  label: t('general.conversations'),
  href: '/conversations',
  // permission: permissions.positionRead,
  count: {
    value:
      menuItemsCounter?.unreadWhatsappCount >= 100
        ? '99+'
        : menuItemsCounter?.unreadWhatsappCount,
    hoverText: `Unread: ${menuItemsCounter?.unreadWhatsappCount}`,
    borderColor:
      menuItemsCounter?.unreadWhatsappCount === 0 ? theme.blue : theme.appleRed,
    backgroundColor:
      menuItemsCounter?.unreadWhatsappCount === 0
        ? theme.persianBlue
        : theme.faluRed,
    className: 'rounded-circle mt-1 text-center',
  },
})
/**
 * Creates the Offers menu item
 * @param {Function} t - Translation function
 * @param {Object} menuItemsCounter - Counter for menu items pending offers
 * @param {Object} permissions - User permissions configuration
 * @returns {Object} Offers form menu item configuration
 */
const createOffersItem = (t, menuItemsCounter, theme, permissions) => ({
  icon: OfferIcon,
  label: t('candidate.offers'),
  href: '/offers',
  permission: permissions.offerRead,
   count: {
    value:
      menuItemsCounter?.pendingOffers >= 100
        ? '99+'
        : menuItemsCounter?.pendingOffers,
    hoverText: `Unread: ${menuItemsCounter?.pendingOffers}`,
    borderColor:
      menuItemsCounter?.pendingOffers === 0 ? theme.blue : theme.appleRed,
    backgroundColor:
      menuItemsCounter?.pendingOffers === 0
        ? theme.persianBlue
        : theme.faluRed,
    className: 'rounded-circle mt-1 text-center',
  },
})

/**
 * Creates the strategic planner section
 * @param {Function} t - Translation function
 * @param {Object} permissions - User permissions configuration
 * @returns {Object} Strategic planner section configuration
 */
const createStrategicPlannerSection = (t, permissions) => ({
  section: t('layout.strategicPlanner'),
  items: [
    {
      icon: HierarchyIcon,
      label: t('workforce.workforcePlanning'),
      subItems: [
        {
          icon: HierarchyIcon,
          label: t('organizationChart.chart'),
          href: '/workforce-structure/chart',
          permission: permissions.reportsSourcing,
        },
        {
          icon: DatabaseIcon,
          label: t('organizationChart.table'),
          href: '/workforce-structure/table',
          permission: permissions.reportsSourcing,
        },
        {
          icon: ChartIcon,
          label: t('layout.workforceCalculator'),
          href: '/workforce-structure/calculator',
          permission: permissions.reportsSourcing,
        },
      ],
    },
  ],
})

/**
 * Creates the organization section
 * @param {Function} t - Translation function
 * @param {String} organizationType - Type of organization
 * @param {Boolean} isAgency - Whether the user belongs to an agency
 * @param {Object} menuItemsCounter - Counter for menu items notifications
 * @param {Object} theme - Theme constants for styling
 * @param {Object} permissions - User permissions configuration
 * @returns {Object} Organization section configuration
 */
const createOrganizationSection = (
  t,
  organizationType,
  isAgency,
  menuItemsCounter,
  theme,
  permissions,
) => ({
  section: t('layout.organization', { organizationType }),
  items: [
    createReportsItem(t, isAgency, permissions),
    createCalendarItem(t, permissions),
    createSettingsItem(t, permissions),
    createDataManagementItem(t, permissions),
  ],
})

/**
 * Creates the reports menu item
 * @param {Function} t - Translation function
 * @param {Boolean} isAgency - Whether the user belongs to an agency
 * @param {Object} permissions - User permissions configuration
 * @returns {Object} Reports menu item configuration
 */
const createReportsItem = (t, isAgency, permissions) => ({
  icon: ChartIcon,
  label: t('general.reports'),
  subItems: [
    {
      icon: ChartIcon,
      label: t('pages.sourcing'),
      href: '/reports/sourcing',
      permission: permissions.reportsSourcing,
    },
    {
      icon: ChartIcon,
      label: t('reports.qualityOfHire'),
      permission: permissions.reportsSourcing,
      subItems: [
        {
          icon: ChartIcon,
          label: t('reports.timeToFill'),
          href: '/reports/time-to-fill',
          permission: permissions.reportsSourcing,
        },
        {
          icon: ChartIcon,
          label: t('reports.timeToHire'),
          href: '/reports/time-to-hire',
          permission: permissions.reportsSourcing,
        },
        {
          icon: ChartIcon,
          label: t('reports.interviewToHire.menuLabel'),
          href: '/reports/interview-to-hire',
          permission: permissions.reportsSourcing,
        },
      ],
    },
    {
      icon: ChartIcon,
      label: t('reports.performanceReports'),
      permission: permissions.reportsSourcing,
      subItems: [
        {
          icon: ChartIcon,
          label: t('reports.hiringManager.menuLabel'),
          href: '/reports/hiring-manager-performance',
          permission: permissions.reportsSourcing,
        },
        {
          icon: ChartIcon,
          label: t('reports.recruiter.menuLabel'),
          href: '/reports/recruiter-performance',
          permission: permissions.reportsSourcing,
        },
        {
          icon: ChartIcon,
          label: t('reports.vacancyRate.menuLabel'),
          href: '/reports/vacancy-rate',
          permission: permissions.reportsSourcing,
        },
        {
          icon: ChartIcon,
          label: t('reports.offerAcceptance.menuLabel'),
          href: '/reports/offer-acceptance-rate',
          permission: permissions.reportsSourcing,
        },
      ],
    },
    {
      icon: ChartIcon,
      label: t('reports.requisitionAnalysis.groupLabel'),
      permission: permissions.reportsSourcing,
      subItems: [
        {
          icon: ChartIcon,
          label: t('reports.requisitionAging.menuLabel'),
          href: '/reports/requisition-aging',
          permission: permissions.reportsSourcing,
        },
      ],
    },
    {
      icon: ChartIcon,
      label: t('reports.charts'),
      permission: permissions.reportsSourcing,
      subItems: [
        {
          icon: ChartIcon,
          label: t('reports.positionCharts'),
          href: '/reports/charts/position',
          permission: permissions.reportsSourcing,
        },
        {
          icon: ChartIcon,
          label: t('reports.candidateCharts'),
          href: '/reports/charts/candidate',
          permission: permissions.reportsSourcing,
        },
        {
          icon: ChartIcon,
          label: t('reports.applicationCharts'),
          href: '/reports/charts/application',
          permission: permissions.reportsSourcing,
        },
        {
          icon: ChartIcon,
          label: t('reports.interviewCharts'),
          href: '/reports/charts/interview',
          permission: permissions.reportsSourcing,
        },
        {
          icon: ChartIcon,
          label: t('reports.requisitionCharts'),
          href: '/reports/charts/requisition',
          permission: permissions.reportsSourcing,
        },
        ...(isAgency
          ? [
            {
              icon: ChartIcon,
              label: t('reports.agencyCharts'),
              href: '/reports/charts/agency',
              permission: permissions.reportsSourcing,
            },
          ]
          : []),
      ],
    },
  ],
})
/**
 * Creates the calendar menu item
 * @param {Function} t - Translation function
 * @param {Object} permissions - User permissions configuration
 * @returns {Object} Reports menu item configuration
 */
const createCalendarItem = (t, permissions) => ({
  icon: CalendarIcon,
  label: t('general.calendar'),
  subItems: [
    {
      icon: ChartIcon,
      label: t('reports.scheduling'),
      href: '/interview-schedule',
      permission: permissions.reportsInterviewSchedule,
    },
    {
      icon: ChartIcon,
      label: t('reports.personalCalendar'),
      href: '/personal-calendar',
      permission: permissions.userPersonalCalendar,
    },
  ],
})

/**
 * Creates the settings menu item
 * @param {Function} t - Translation function
 * @param {Object} permissions - User permissions configuration
 * @returns {Object} Settings menu item configuration
 */
const createSettingsItem = (t, permissions) => ({
  icon: GearIcon,
  label: t('layout.settings'),
  subItems: [
    {
      icon: GearIcon,
      label: t('layout.profile'),
      href: '/profile',
      permission: permissions.companyRead,
    },
    {
      icon: GearIcon,
      label: t('layout.configuration'),
      href: '/configuration',
      permission: permissions.roleReadAll,
    },
    {
      icon: GearIcon,
      label: t('layout.defaults'),
      // TODO: Add the suitable permission for the defaults menu item
      permission: permissions.userReadAll,
      subItems: [
        {
          icon: GearIcon,
          label: t('layout.generalDefaults'),
          href: '/defaults',
          permission: permissions.userReadAll,
        },
        {
          icon: GearIcon,
          label: t('layout.system'),
          permission: permissions.userReadAll,
          subItems: [
            {
              icon: GearIcon,
              label: t('layout.budgetConfig'),
              href: '/budget-configuration',
              permission: permissions.userReadAll,
            },
            {
              icon: GearIcon,
              label: t('layout.scoreConfigurationDefaults'),
              href: '/system-defaults/score-configuration',
              permission: permissions.userReadAll,
            },
            {
              icon: GearIcon,
              label: t('layout.dropdownsDefaults'),
              href: '/system-defaults/dropdowns',
              permission: permissions.userReadAll,
            },
            {
              icon: GearIcon,
              label: t('layout.templates'),
              href: '/system-defaults/templates',
              permission: permissions.jobTemplatesRead,
            },
            {
              icon: GearIcon,
              label: t('layout.referenceFormDefaults'),
              href: '/system-defaults/reference-form-generation',
              permission: permissions.userReadAll,
            },
          ],
        },
      ],
    },
  ],
})

/**
 * Creates the data management menu item
 * @param {Function} t - Translation function
 * @param {Object} permissions - User permissions configuration
 * @returns {Object} Data management menu item configuration
 */
const createDataManagementItem = (t, permissions) => ({
  icon: DatabaseIcon,
  label: t('layout.dataManagement'),
  href: '/data-management',
  permission: permissions.dataManagementRead,
})

/**
 * Creates the organization chart item
 * @param {Function} t - Translation function
 * @param {Object} permissions - User permissions configuration
 * @returns {Object} Organization chart
 */
const createOrganizationChartItem = (t, permissions) => ({
  icon: OrgChartIcon,
  label: t('layout.orgChart'),
  href: '/workforce-structure/chart',
  permission: permissions.reportsSourcing,
})

